import React, { Fragment, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { v4 } from "uuid";

import IconButton from "@cayuse/shared-react.components.icon-button";
import { crunchClasses } from "@cayuse/shared-react.utils.helpers";

import ExpandIcon from "components/BudgetBuilder/shared/ExpandIcon";
import { useTenantFormatter } from "components/BudgetBuilder/shared/TenantFormatter";
import SubawardLineItemRow from "components/BudgetBuilder/SponsorCosts/LineItemsList/SubawardLineItemRow";
import Table from "components/Table";
import { budgetBuilderSelectors, tenantSelectors } from "store/selectors";
import {
	getDirectCostSubtotal,
	getSubawardCategoriesByValue,
	getSubawardsFACostTotalForPeriod,
	toNumber,
} from "utils/budgetBuilder";

import styles from "./styles.css";


export const SubawardLineItem = ({ editedSponsoredCostsByPeriod, lineItem, isEditing, onChange }) => {
	const { currencyFormatter } = useTenantFormatter();
	const [isExpanded, setIsExpanded] = useState(true);

	const handleIsExpandedChange = () => {
		setIsExpanded(!isExpanded);
		for (let item of lineItem.items) {
			item.isExpanded = !item.isExpanded;
		}
	};

	const { subawardCostCap } = useSelector(tenantSelectors.tenant);
	const currentPeriodNumber = useSelector(budgetBuilderSelectors.currentPeriodNumber);
	const sponsoredCostsByPeriod = useSelector(budgetBuilderSelectors.sponsoredCostsByPeriod);
	const currentPeriodRate = useSelector(budgetBuilderSelectors.currentPeriodRate);

	const subawardCategoriesByValue = useMemo(
		() => getSubawardCategoriesByValue(sponsoredCostsByPeriod),
		[ sponsoredCostsByPeriod ]
	);


	const handleRemoveItem = value => {
		let items = [];
		for (let item of lineItem.items) {
			if (item.value !== value) {
				items.push(item);
			}
		}
		onChange({ ...lineItem, items });
	};

	const updateLineItem = (value, newItem) => {
		let items = [ ...lineItem.items ];
		for (let i = 0; i < items.length; i++) {
			let item = items[i];
			if (item.value === value) {
				items[i] = {
					...items[i],
					...newItem
				};
				break;
			}
		}
		onChange({ ...lineItem, items });
	};

	const directCostTotal = useMemo(
		() => getDirectCostSubtotal(lineItem),
		[ lineItem ]
	);

	const faCostTotal = useMemo(
		() => getSubawardsFACostTotalForPeriod(editedSponsoredCostsByPeriod, subawardCostCap, currentPeriodNumber),
		[ currentPeriodNumber, editedSponsoredCostsByPeriod, subawardCostCap ]
	);

	const total = useMemo(
		() => toNumber(directCostTotal) + toNumber(faCostTotal),
		[ directCostTotal, faCostTotal ]
	);

	const addItemToSubawards = () => {
		const newItem = {
			value: "",
			active: true,
			hash: v4(),
			code: "",
			isFA: false,
			FA: currentPeriodRate,
			directCost: 0,
			sort: 0,
			items: null,
			isExpanded: true,
		};
		onChange({
			...lineItem,
			items: [
				...lineItem.items,
				newItem
			]
		});
	};

	const classNames = crunchClasses({
		[styles.row]: true,
		[styles.parentRow]: true,
	});

	return (
		<Fragment>
			<Table.Row className={classNames}>
				<Table.Cell className={styles.expandCell}>
					<span>{"Subawards"}</span>
					<ExpandIcon
						isExpanded={isExpanded}
						onClick={() => handleIsExpandedChange()}
					/>
				</Table.Cell>
				<Table.Cell />
				<Table.Cell />
				<Table.Cell />
				<Table.Cell />
				<Table.Cell />
				<Table.Cell>
					{isEditing && (
						<IconButton
							className={`${styles.rowIcon} ${styles.blue}`}
							icon="plus"
							size="bigger"
							onClick={() => addItemToSubawards()}
							tertiary
						/>
					)}
				</Table.Cell>
			</Table.Row>
			{lineItem?.items && lineItem.items.map((item, index) => (
				<SubawardLineItemRow
					item={item}
					categoriesByPeriod={subawardCategoriesByValue?.[item.value]}
					isEditing={isEditing}
					handleRemoveItem={() => handleRemoveItem(item.value)}
					updateLineItem={newItem => updateLineItem(item.value, newItem)}
					key={index}
				/>
			))}
			<Table.Row className={`${styles.rowHeader} ${styles.row}`}>
				<Table.Cell className={styles.rowHeaderCell}>
					<span className={styles.bold}>{"Subawards Subtotal"}</span>
				</Table.Cell>

				<Table.Cell className={`${styles.rowHeaderCell} ${styles.cellAlignRight}`}>
					<span className={styles.bold}>
						{currencyFormatter(directCostTotal)}
					</span>
				</Table.Cell>
				<Table.Cell className={styles.rowHeaderCell}/>
				<Table.Cell className={styles.rowHeaderCell}/>
				<Table.Cell
					className={`${styles.rowHeaderCell} ${styles.cellAlignRight} ${styles.valueCellPadding}`}
				>
					<span className={styles.bold}>
						{currencyFormatter(faCostTotal)}
					</span>
				</Table.Cell>
				<Table.Cell
					className={`${styles.rowHeaderCell} ${styles.cellAlignRight} ${styles.valueCellPadding}`}
				>
					<span className={styles.bold}>
						{currencyFormatter(total)}
					</span>
				</Table.Cell>
				<Table.Cell className={styles.rowHeaderCell}>
				</Table.Cell>
			</Table.Row>
		</Fragment>
	);
};
